import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiPath } from "../lib/url";
import { splitInterview } from "./Batch";
import type { ArticleSummary, MeResponse } from "../lib/types";

// Script delivery + upload. Freelancer records their own video on
// whatever device they like, then comes here to upload the file.
// No browser-side recording, no teleprompter — just the script and
// a single file input.

interface UploadProgress {
  status: "idle" | "uploading" | "done" | "error";
  pct: number;
  message: string;
}

export default function Record() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const numericId = Number(articleId);

  const [article, setArticle] = useState<ArticleSummary | null>(null);
  const [batchTotal, setBatchTotal] = useState<number>(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [upload, setUpload] = useState<UploadProgress>({
    status: "idle",
    pct: 0,
    message: "",
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setLoadError("Bad article id");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiPath("/api/me"));
        if (res.status === 401) {
          navigate("/", { replace: true });
          return;
        }
        if (!res.ok) {
          setLoadError(`Could not load batch (${res.status})`);
          return;
        }
        const data = (await res.json()) as MeResponse;
        const found = data.articles.find((a) => a.id === numericId);
        if (!cancelled) {
          if (!found) setLoadError("Article not found");
          else {
            setArticle(found);
            setBatchTotal(data.articles.length);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [numericId, navigate]);

  const segments = article ? splitInterview(article.interviewText ?? "") : [];
  const targetDuration = article?.durationSeconds ?? 60;

  // Plain-text version of the script for the clipboard. One line per
  // Q/A. Freelancers can paste this into a Notes app on their phone
  // while recording.
  function buildPlainScript(): string {
    if (!article) return "";
    const lines: string[] = [];
    lines.push(article.workingTitle || article.sourceTitle || "");
    lines.push(`Target read length: ${targetDuration} seconds`);
    lines.push("");
    for (const seg of segments) {
      if (seg.q) lines.push(`Q: ${seg.q}`);
      lines.push(`A: ${seg.a}`);
      lines.push("");
    }
    return lines.join("\n").trim();
  }

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(buildPlainScript());
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1600);
    } catch {
      // Older browsers — fallback to a hidden textarea.
      const ta = document.createElement("textarea");
      ta.value = buildPlainScript();
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopyState("copied");
        window.setTimeout(() => setCopyState("idle"), 1600);
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  function chooseFile() {
    fileInputRef.current?.click();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) return;
    if (!picked.type.startsWith("video/")) {
      setUpload({
        status: "error",
        pct: 0,
        message: `That doesn't look like a video file (got "${picked.type || "unknown"}").`,
      });
      return;
    }
    setFile(picked);
    setUpload({ status: "idle", pct: 0, message: "" });
  }

  async function uploadFile() {
    if (!file || !article) return;
    setUpload({ status: "uploading", pct: 0, message: "Uploading…" });
    try {
      const xhr = new XMLHttpRequest();
      xhr.open(
        "POST",
        apiPath(`/api/upload?articleId=${encodeURIComponent(String(article.id))}`),
        true,
      );
      xhr.setRequestHeader("content-type", file.type || "video/mp4");
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const pct = Math.floor((e.loaded / e.total) * 100);
          setUpload((u) => ({ ...u, pct }));
        }
      });
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUpload({ status: "done", pct: 100, message: "Uploaded." });
          window.setTimeout(() => {
            navigate("/batch", { replace: true });
          }, 900);
        } else {
          let detail = `HTTP ${xhr.status}`;
          try {
            const body = JSON.parse(xhr.responseText);
            if (body?.error) detail = body.error;
          } catch {
            // ignore
          }
          setUpload({
            status: "error",
            pct: 0,
            message: `Upload failed: ${detail}`,
          });
        }
      };
      xhr.onerror = () => {
        setUpload({
          status: "error",
          pct: 0,
          message: "Upload failed. Check your connection and try again.",
        });
      };
      xhr.send(file);
    } catch (err) {
      setUpload({
        status: "error",
        pct: 0,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (loadError) {
    return (
      <div className="batch-shell">
        <div className="errstate">{loadError}</div>
        <Link to="/batch" className="btn ghost">Back to batch</Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="batch-shell">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const alreadyUploaded = !!article.rawVideoKey;

  return (
    <div className="batch-shell">
      <div className="batch-topbar">
        <div className="batch-brand">
          <span className="mark" />
          Flywheel Studio
        </div>
        <Link to="/batch" className="btn ghost" style={{ padding: "8px 14px", fontSize: 13 }}>
          Back to batch
        </Link>
      </div>

      <div className="script-header">
        <div className="article-position">
          Video {article.position} of {batchTotal || article.position}
          {alreadyUploaded && (
            <>
              {" · "}
              <span className="status-pill done">uploaded</span>
            </>
          )}
        </div>
        <h1 className="batch-title" style={{ marginTop: 6 }}>
          {article.workingTitle || article.sourceTitle || "(untitled)"}
        </h1>
      </div>

      <div className="article-card" style={{ marginTop: 16 }}>
        <div className="script-section-header">
          <div className="interview-block-label" style={{ margin: 0 }}>
            The script
          </div>
          <button
            type="button"
            className="btn ghost btn-small"
            onClick={copyScript}
          >
            {copyState === "copied" ? "Copied" : "Copy script"}
          </button>
        </div>

        {segments.length === 0 ? (
          <div className="errstate">No script for this article yet.</div>
        ) : (
          <div className="interview-block">
            {segments.map((seg, i) => (
              <div key={i}>
                {seg.q && <p className="interview-q">{seg.q}</p>}
                <p className="interview-a">{seg.a}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="article-card" style={{ marginTop: 16 }}>
        <div className="interview-block-label">Upload your video</div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: "none" }}
          onChange={onFileChange}
        />

        {!file && (
          <button
            type="button"
            className="btn primary"
            onClick={chooseFile}
            style={{ marginTop: 8 }}
          >
            {alreadyUploaded ? "Choose new file (replace)" : "Choose video file"}
          </button>
        )}

        {file && upload.status !== "uploading" && upload.status !== "done" && (
          <>
            <div className="file-preview">
              <div className="file-preview-name">{file.name}</div>
              <div className="file-preview-meta">
                {humanSize(file.size)} · {file.type || "video"}
              </div>
            </div>
            <div className="article-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="btn ghost"
                onClick={chooseFile}
              >
                Pick a different file
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={uploadFile}
              >
                Upload {humanSize(file.size)}
              </button>
            </div>
          </>
        )}

        {upload.status === "uploading" && (
          <div style={{ marginTop: 14 }}>
            <div className="bar">
              <div style={{ width: `${upload.pct}%` }} />
            </div>
            <div style={{ marginTop: 8, color: "var(--ink-3)", fontSize: 13 }}>
              Uploading {humanSize(file?.size ?? 0)} · {upload.pct}%
            </div>
          </div>
        )}

        {upload.status === "done" && (
          <div className="upload-done">
            Uploaded. Returning to your batch…
          </div>
        )}

        {upload.status === "error" && (
          <>
            <div className="errstate" style={{ marginTop: 12 }}>
              {upload.message}
            </div>
            {file && (
              <div className="article-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={chooseFile}
                >
                  Pick a different file
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={uploadFile}
                >
                  Retry upload
                </button>
              </div>
            )}
          </>
        )}

        <p className="upload-help">
          Most video file types work. Keep it under 500 MB please.
        </p>
      </div>
    </div>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
